import { parseSanitizedHtml } from "@/utils/sanitizeHtml";
import Adjective from './Adjective';
import { useEffect, useRef } from "react";
import Helpers from '../Helpers';
import {
   adminDigitToRating,
   applyChecklistRatingDigit,
   formatChecklistRatingDisplay,
   isValidChecklistRating,
   resolveRatingInput,
   shouldAdvanceChecklistRatingEntry,
} from './checklistRating';

export default function Adjectives(props) {
   const { sectionData, values } = props;
   const currentSection = sectionData?.currentSection;
   const containerRef = useRef(null);
   const languageContent = Helpers.getSectionContent(currentSection, sectionData?.lang);
   const elements = Helpers.getSectionElements(languageContent);
   const instructionsHtml = Helpers.getSectionHtmlValue(languageContent, "instructions");
   const headers = Array.isArray(elements?.headers) ? elements.headers : [];
   const items = Array.isArray(elements?.items) ? elements.items : [];
   const ratingInput = resolveRatingInput(elements);

   function getIssueCount(validationValues) {
      let localIssueCount = 0;
      const adjectiveCount = Number(items?.length || 0);
      for (let i = 1; i <= adjectiveCount; i++) {
         let cid = currentSection.formId + i + 'c';
         if (validationValues[cid] === undefined || validationValues[cid] === '')
            localIssueCount++;

         let sid = currentSection.formId + i + 'r';
         if (validationValues[sid] === undefined || validationValues[sid] === 0)
            localIssueCount++;
      }
      return localIssueCount;
   }

   useEffect(() => {
      if (!isOnTab()) return;
      sectionData.registerSection( 767);
   }, [sectionData?.currentSection?.name]);

   useEffect(() => {
      if (!isOnTab()) return;
      const nextCount = Number(sectionData?.nextCount || 0);
      const issueCount = nextCount > 0 ? getIssueCount(values || {}) : 0;
      sectionData?.updateIssueCount?.(issueCount);
   }, [items?.length, sectionData?.nextCount, sectionData?.updateIssueCount, values]);

   useEffect(() => {
      if (!isOnTab() || !props?.sectionData?.isAdmin || !containerRef.current) return;
      if (ratingInput !== "input") return;
      const firstInput = containerRef.current.querySelector('input.checklistRatingInput') as HTMLInputElement | null;
      firstInput?.focus();
   }, [sectionData?.currentSection?.name, sectionData?.isAdmin, ratingInput]);

   useEffect(() => {
      if (isOnTab()) {
         const handleKeyPress = (e) => {
            if (!props?.sectionData?.isAdmin) return;

            const activeTag = document.activeElement?.tagName?.toLowerCase();
            if (activeTag === "textarea" || activeTag === "select") return;

            if (activeTag === "input" && (e.key === "ArrowUp" || e.key === "ArrowDown")) {
               e.preventDefault();
               return;
            }

            if (ratingInput === "input" && activeTag === "input" && /^[0-9]$/.test(e.key)) {
               e.preventDefault();
               const containerEl = containerRef.current;
               if (!containerEl) return;
               const inputs = Array.from(containerEl.querySelectorAll('input.checklistRatingInput')) as HTMLInputElement[];
               const currentInput = document.activeElement as HTMLInputElement;
               const currentIndex = inputs.indexOf(currentInput);
               if (currentIndex < 0) return;

               const currentDisplay = currentInput.value || "";
               const numericValue = applyChecklistRatingDigit(currentDisplay, e.key);

               const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
               nativeValueSetter?.call(currentInput, formatChecklistRatingDisplay(numericValue));
               currentInput.dispatchEvent(new Event('input', { bubbles: true }));

               if (shouldAdvanceChecklistRatingEntry(currentDisplay, numericValue)
                  && currentIndex + 1 < inputs.length) {
                  inputs[currentIndex + 1].focus();
                  inputs[currentIndex + 1].select();
               }
               return;
            }

            const key = (e.key || "").toLowerCase();
            const containerEl = containerRef.current;
            if (!containerEl) return;

            if (ratingInput !== "input" && /^[0-9]$/.test(e.key)) {
               e.preventDefault();
               const value = adminDigitToRating(e.key);
               if (value === null) return;

               for (let i = 1; i <= items.length; i++) {
                  const sid = currentSection.formId + i + "r";
                  const rating = Number(values?.[sid] || 0);
                  if (!isValidChecklistRating(rating)) {
                     void sectionData.onAnswer(String(i) + "r", value);
                     break;
                  }
               }
               return;
            }

            if (ratingInput === "input" && /^[0-9]$/.test(key)) {
               e.preventDefault();

               const value = applyChecklistRatingDigit("", key);

               const numberInputs = Array.from(containerEl.querySelectorAll('input.checklistRatingInput')) as HTMLInputElement[];
               const firstUnfilledInput = numberInputs.find((input) => {
                  const currentValue = (input.value || '').trim();
                  return currentValue === '';
               });

               if (firstUnfilledInput) {
                  const nativeValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set;
                  nativeValueSetter?.call(firstUnfilledInput, formatChecklistRatingDisplay(value));
                  firstUnfilledInput.dispatchEvent(new Event('input', { bubbles: true }));
               }
            }
            else if (key === 't' || key === 's') {
               e.preventDefault();

               // Find all table rows (skip header)
               const tableRows = containerEl.querySelectorAll('tbody tr');

               // Look for first row without trait/skill selection
               for (let i = 0; i < tableRows.length; i++) {
                  const row = tableRows[i];
                  const cells = row.children;

                  if (cells.length >= 5) {
                     const traitCell = cells[3]; // 4th column (0-indexed)
                     const skillCell = cells[4]; // 5th column (0-indexed)

                     // Check if neither trait nor skill is selected (no 'checked' class)
                     const traitSelected = traitCell.classList.contains('checked');
                     const skillSelected = skillCell.classList.contains('checked');

                     if (!traitSelected && !skillSelected) {
                        // This row needs trait/skill selection
                        const targetCell = key === 't' ? traitCell : skillCell;
                        (targetCell as HTMLElement).click();
                        break;
                     }
                  }
               }

            }
         };

         // Listen at document level so shortcuts still work after focus moves to controls (e.g. Next button).
         document.addEventListener('keydown', handleKeyPress);

         return () => {
            document.removeEventListener('keydown', handleKeyPress);
         };
      }
   }, [sectionData?.currentSection?.name, sectionData?.isAdmin, ratingInput, items?.length, values]);

   function isOnTab() { return currentSection?.formId === 300; }
   
   if(!isOnTab()) return;

   async function onChange(childId, value) {
      await sectionData.onAnswer(childId, value);
   }

   return (

      <div className="adjectives" ref={containerRef} tabIndex={0}>
         {instructionsHtml && (
         <div className="instructions left">{parseSanitizedHtml(instructionsHtml || "")}</div>
         )}
         <div className="SectionBody">

         <table className="tableData nowrap highlightEven">
            <thead>
               <tr>
                  <th className="rating columnheader columndata" data-column="1">{headers[0] || ""}</th>
                  <th className="adjective columnheader columndata" data-column="2">{headers[1] || ""}</th>
                  <th>{headers[2] || ""}</th>
                  <th className="trait columnheader columndata" data-column="4">{headers[3] || ""}</th>
                  <th className="skill columnheader columndata" data-column="5">{headers[4] || ""}</th>
               </tr>
            </thead>
            <tbody data-page="1">
               {items?.map((a, i) => {
                  // Create isolated values object for this specific adjective
                  const adjectiveIndex = i + 1; // 1-based index
                  const ratingKey = currentSection?.formId + adjectiveIndex + 'r';
                  const categoryKey = currentSection?.formId + adjectiveIndex + 'c';
                  
                  const adjectiveValues = {
                     r: values[ratingKey] || 0,
                     c: values[categoryKey] || ''
                  };
                  
                  return (
                     <Adjective
                        key={i}
                        id={a.id}
                        section={currentSection}
                        name={a.name}
                        description={a.description}
                        isAdmin={sectionData?.isAdmin}
                        showValidations={sectionData?.showValidations}
                        formId={currentSection?.formId}
                        value={adjectiveValues}
                        setValue={onChange}
                        ratingInput={ratingInput}
                        elements={elements}
                     />
                  );
               })}
            </tbody>
         </table>
      </div>
      </div>
   );
}



